import { TranslationFile } from 'i18n'
import { useTranslation } from 'react-i18next'

import appConfig from 'app.config'
import {
	GetAllCitizenshipsWithTranslation,
	GetAllCountriesWithTranslation,
} from 'utils/countryHelpers'
import { formatToStringLocale } from 'utils/dateAndTimeHelpers'
import { Gender } from 'constants/enums/genderTypes'
import { FormatEnumToList } from 'utils/commonhelpers'
import { MaritalStatus } from 'constants/enums/maritalStatusTypes'
import { PropertyType } from 'constants/enums/propertyTypes'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { yearsAgo } from 'formBuilder/src'
import { Title } from 'constants/enums/titleTypes'
import { REGEX } from 'constants/values/commonRegExps'
import { AccommodationType } from 'constants/enums/accommodationTypes'
import { Industry } from 'constants/enums/industries'
import { JobTitle } from 'constants/enums/jobTitleTypes'
import { OccupationType } from 'constants/enums/occupationTypes'

export const ApplicationDetailsFormUK = (data: IApplicationInformation) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Countries,
	])

	const formerAddressVisible = (): boolean =>
		!(
			!data?.formerAddress?.buildingName &&
			!data?.formerAddress?.city &&
			!data?.formerAddress?.country &&
			!data?.formerAddress?.county &&
			!data?.formerAddress?.flatNumber &&
			!data?.formerAddress?.street &&
			!data?.formerAddress?.streetNumber &&
			!data?.formerAddress?.timeAtAddress &&
			!data?.formerAddress?.zip
		)

	return {
		fieldSetName: '',
		formName: 'ApplicationDetailsForm',
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				controlName: 'firstName',
				type: 'text',
				label: t('forms:FirstName'),
				value: data?.personalInfo?.firstName,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: true,
				visible: true,
				col: 12,
				viewGroup: {
					id: 'fullName',
					label: t('forms:FullName'),
					isPrimary: true,
					displayFields: ['firstName', 'lastName'],
					formatValue: (values: Record<string, string>) =>
						[values.firstName, values.lastName]
							.filter(Boolean)
							.join(' '),
				},
			},
			{
				controlName: 'lastName',
				type: 'text',
				label: t('forms:LastName'),
				value: data?.personalInfo?.lastName,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: true,
				visible: true,
				col: 12,
				viewGroup: { id: 'fullName', isPrimary: false },
			},
			{
				controlName: 'birthDate',
				type: 'datePicker',
				label: t('forms:BirthDate'),
				minDate: yearsAgo(150),
				maxDate: new Date(), // today
				value: data?.personalInfo?.birthDate,
				required: true,
				visible: true,
				col: 12,
			},
			{
				controlName: 'email',
				type: 'text',
				label: t('forms:Email'),
				value: data?.contacts?.email,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.EMAIL_ADDRESS,
						message: t('forms:ValidationMsgEmail'),
					},
				],
				required: true,
				visible: true,
				col: 12,
			},
			// {
			// 	controlName: 'cellPhone',
			// 	type: 'text',
			// 	label: t('forms:MobilePhoneNo'),
			// 	value: data?.contacts?.cellPhone,
			// 	validations: [
			// 		{
			// 			code: 'MustMatchPattern',
			// 			value: REGEX.PHONE_NUMBER,
			// 			message: t('forms:ValidationMsgPhoneNumber'),
			// 		},
			// 		{
			// 			code: 'MustMatchPattern',
			// 			value: REGEX.UK_PHONE_NUMBER,
			// 			message: t('forms:ValidationMsgUKPhoneNumber'),
			// 		},
			// 	],
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// 	inputPrefix: '+44 0',
			// },
			{
				controlName: 'flatNumber',
				type: 'text',
				label: t('forms:FlatNumber'),
				value: data?.address?.flatNumber,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,30}$', // 0-30 chars
						message: t('ValidationMsgMaxNChars', { n: 30 }),
					},
				],
				required: false,
				visible: true,
				col: 12,
				viewGroup: { id: 'currentAddress', isPrimary: false },
			},
			{
				controlName: 'buildingName',
				type: 'text',
				label: t('forms:BuildingName'),
				value: data?.address?.buildingName,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^.{0,30}$', // 0-30 chars
						message: t('ValidationMsgMaxNChars', { n: 30 }),
					},
				],
				required: false,
				visible: true,
				col: 12,
				viewGroup: { id: 'currentAddress', isPrimary: false },
			},
			{
				controlName: 'streetNumber',
				type: 'text',
				label: t('forms:StreetNumber'),
				value: data?.address?.streetNumber,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^.{0,10}$', // 0-10 chars
						message: t('ValidationMsgMaxNChars', { n: 10 }),
					},
				],
				required: false,
				visible: true,
				col: 12,
				viewGroup: {
					id: 'currentAddress',
					label: t('forms:CurrentAddress'),
					isPrimary: true,
					displayFields: [
						'streetNumber',
						'street',
						'flatNumber',
						'buildingName',
						'city',
						'zip',
						// 'countryOfResidence',
					],
					formatValue: (values: Record<string, string>) => {
						const flatPart = [
							values.flatNumber,
							values.buildingName,
						]
							.filter(Boolean)
							.join(', ')
						const streetPart = [values.streetNumber, values.street]
							.filter(Boolean)
							.join(' ')
						// const countryOptions = GetAllCountriesWithTranslation(
						// 	appConfig.country,
						// )
						// const countryLabel =
						// 	countryOptions.find(
						// 		(c: { value: string; label: string }) =>
						// 			c.value === values.countryOfResidence,
						// 	)?.label ?? values.countryOfResidence
						return [
							flatPart,
							streetPart,
							values.city,
							values.zip,
							// countryLabel,
						]
							.filter(Boolean)
							.join(', ')
					},
				},
			},
			{
				controlName: 'street',
				type: 'text',
				label: t('forms:Street'),
				value: data?.address?.street,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: true,
				visible: true,
				col: 12,
				viewGroup: { id: 'currentAddress', isPrimary: false },
			},
			{
				controlName: 'city',
				type: 'text',
				label: t('forms:City'),
				value: data?.address?.city,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: true,
				visible: true,
				col: 12,
				viewGroup: { id: 'currentAddress', isPrimary: false },
			},
			{
				controlName: 'zip',
				type: 'text',
				label: t('forms:Zip'),
				value: data?.address?.zip,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,10}$', // 0-10 chars
						message: t('ValidationMsgMaxNChars', { n: 10 }),
					},
				],
				required: true,
				visible: true,
				col: 12,
				viewGroup: { id: 'currentAddress', isPrimary: false },
			},

			/* {
				controlName: 'countryOfResidence',
				type: 'dropdown',
				label: t('forms:CountryOfResidence'),
				options: GetAllCountriesWithTranslation(appConfig.country),
				value: data?.address?.countryOfResidence,
				required: true,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
				viewGroup: { id: 'currentAddress', isPrimary: false },
			}, */
			{
				controlName: 'timeAtAddress',
				type: 'monthPicker',
				label: t('forms:TimeAtAddress'),
				value: data?.address?.timeAtAddress,
				minDate: yearsAgo(150),
				maxDate: new Date(), // today
				displayFixedLabel: true,
				required: true,
				visible: true,
				col: 12,
			},
			// {
			// 	controlName: 'gender',
			// 	type: 'dropdown',
			// 	label: t('forms:Gender'),
			// 	options: FormatEnumToList(
			// 		Gender,
			// 		PropertyType.Gender,
			// 		TranslationFile.Dropdown,
			// 	),
			// 	value: data?.personalInfo?.gender,
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownGender'),
			// },
			// {
			// 	controlName: 'title',
			// 	type: 'dropdown',
			// 	label: t('forms:Title'),
			// 	options: FormatEnumToList(
			// 		Title,
			// 		PropertyType.Title,
			// 		TranslationFile.Dropdown,
			// 	),
			// 	value: data?.personalInfo?.title,
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownTitle'),
			// },
			// {
			// 	controlName: 'maritalStatus',
			// 	type: 'dropdown',
			// 	label: t('forms:MaritalStatus'),
			// 	options: FormatEnumToList(
			// 		MaritalStatus,
			// 		PropertyType.MaritalStatus,
			// 		TranslationFile.Dropdown,
			// 	),
			// 	value: data?.personalInfo?.maritalStatus,
			// 	required: false,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownMaritalStatus'),
			// },
			// {
			// 	controlName: 'countryOfBirth',
			// 	type: 'dropdown',
			// 	label: t('forms:CountryOfBirth'),
			// 	options: GetAllCountriesWithTranslation(appConfig.country),
			// 	value: data?.personalInfo?.countryOfBirth,
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			// },
			// {
			// 	controlName: 'cityOfBirth',
			// 	type: 'input',
			// 	label: t('forms:CityOfBirth'),
			// 	displayFixedLabel: true,
			// 	value: data?.personalInfo?.cityOfBirth,
			// 	validations: [
			// 		{
			// 			code: 'MustMatchPattern',
			// 			value: REGEX.NO_SPECIAL_CHARS,
			// 			message: t('forms:ValidationMsgUnsupportedChars'),
			// 		},
			// 		{
			// 			code: 'MustMatchPattern',
			// 			value: '^.{0,100}$', // 0-100 chars
			// 			message: t('ValidationMsgMaxNChars', { n: 100 }),
			// 		},
			// 	],
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// },
			// {
			// 	controlName: 'nationality',
			// 	type: 'dropdown',
			// 	label: t('forms:Nationality'),
			// 	value: data?.personalInfo?.nationality,
			// 	options: GetAllCountriesWithTranslation(appConfig.country),
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			// },
			// {
			// 	controlName: 'citizenship',
			// 	type: 'dropdown',
			// 	label: t('forms:Citizenship'),
			// 	value: data?.personalInfo?.citizenship?.join(',') || '',
			// 	options: GetAllCitizenshipsWithTranslation(appConfig.country),
			// 	multiple: true,
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			// },
			{
				controlName: 'formerFlatNumber',
				type: 'text',
				label: t('forms:FlatNumber'),
				value: data?.formerAddress?.flatNumber,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,30}$', // 0-30 chars
						message: t('ValidationMsgMaxNChars', { n: 30 }),
					},
				],
				required: false,
				visible: formerAddressVisible(),
				col: 12,
				viewGroup: { id: 'formerAddress', isPrimary: false },
			},
			{
				controlName: 'formerBuildingName',
				type: 'text',
				label: t('forms:BuildingName'),
				value: data?.formerAddress?.buildingName,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^.{0,30}$', // 0-30 chars
						message: t('ValidationMsgMaxNChars', { n: 30 }),
					},
				],
				required: false,
				visible: formerAddressVisible(),
				col: 12,
				viewGroup: { id: 'formerAddress', isPrimary: false },
			},
			{
				controlName: 'formerStreetNumber',
				type: 'text',
				label: t('forms:StreetNumber'),
				value: data?.formerAddress?.streetNumber,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^.{0,10}$', // 0-10 chars
						message: t('ValidationMsgMaxNChars', { n: 10 }),
					},
				],
				required: false,
				visible: formerAddressVisible(),
				col: 12,
				viewGroup: {
					id: 'formerAddress',
					label: t('forms:FormerAddress'),
					isPrimary: true,
					visible: formerAddressVisible(),
					displayFields: [
						'formerStreetNumber',
						'formerStreet',
						'formerFlatNumber',
						'formerBuildingName',
						'formerCity',
						'formerZip',
					],
					formatValue: (values: Record<string, string>) => {
						const flatPart = [
							values.formerFlatNumber,
							values.formerBuildingName,
						]
							.filter(Boolean)
							.join(', ')
						const streetPart = [
							values.formerStreetNumber,
							values.formerStreet,
						]
							.filter(Boolean)
							.join(' ')
						const countryOptions = GetAllCountriesWithTranslation(
							appConfig.country,
						)
						const countryLabel =
							countryOptions.find(
								(c: { value: string; label: string }) =>
									c.value === values.formerCountryOfResidence,
							)?.label ?? values.formerCountryOfResidence
						return [
							flatPart,
							streetPart,
							values.formerCity,
							values.formerZip,
							countryLabel,
						]
							.filter(Boolean)
							.join(', ')
					},
				},
			},
			{
				controlName: 'formerStreet',
				type: 'text',
				label: t('forms:Street'),
				value: data?.formerAddress?.street,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: false,
				visible: formerAddressVisible(),
				col: 12,
				viewGroup: { id: 'formerAddress', isPrimary: false },
			},
			{
				controlName: 'formerCity',
				type: 'text',
				label: t('forms:City'),
				value: data?.formerAddress?.city,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: false,
				visible: formerAddressVisible(),
				col: 12,
				viewGroup: { id: 'formerAddress', isPrimary: false },
			},
			{
				controlName: 'formerZip',
				type: 'text',
				label: t('forms:Zip'),
				value: data?.formerAddress?.zip,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: REGEX.NO_SPECIAL_CHARS_OR_CYRILLIC,
						message: t(
							'forms:ValidationMsgUnsupportedCharsInclCyrillic',
						),
					},
					{
						code: 'MustMatchPattern',
						value: '^.{0,10}$', // 0-10 chars
						message: t('ValidationMsgMaxNChars', { n: 10 }),
					},
				],
				required: false,
				visible: formerAddressVisible(),
				col: 12,
				viewGroup: { id: 'formerAddress', isPrimary: false },
			},
			{
				controlName: 'formerTimeAtAddress',
				type: 'monthPicker',
				label: t('forms:TimeAtAddress'),
				value: data?.formerAddress?.timeAtAddress,
				minDate: yearsAgo(150),
				maxDate: new Date(), // today
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustBeLessOtherControl',
						value: 'timeAtAddress',
						message: t('forms:ValidationTimeAtAddressFormer'),
					},
				],
				required: true,
				visible: formerAddressVisible(),
				col: 12,
			},
			{
				controlName: 'accomodationType',
				type: 'dropdown',
				label: t('forms:AccommodationType'),
				options: FormatEnumToList(
					AccommodationType,
					PropertyType.AccommodationType,
					TranslationFile.Dropdown,
				),
				value: data.accommodation?.type,
				required: true,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownAccomodation'),
			},
			{
				controlName: 'occupationType',
				type: 'dropdown',
				label: t('forms:Occupation'),
				options: FormatEnumToList(
					OccupationType,
					PropertyType.Occupation,
					TranslationFile.Dropdown,
				),
				value: data.employment?.occupationType,
				required: true,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownOccupationType'),
			},
			{
				controlName: 'monthlyNetIncome',
				type: 'number',
				label: t('forms:Income'),
				value: data.employment?.monthlyNetIncome,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustBeGreaterOrEqual',
						value: 0,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
					{
						code: 'MustBeLessOrEqual',
						value: 1e8,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
				],
				required: true,
				visible: true,
				col: 12,
			},
			{
				controlName: 'otherIncome',
				type: 'text',
				label: t('forms:OtherIncome'),
				value: data.employment?.otherIncome,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustBeGreaterOrEqual',
						value: 0,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
					{
						code: 'MustBeLessOrEqual',
						value: 1e8,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
				],
				required: true,
				visible: true,
				col: 12,
			},
			{
				controlName: 'cost',
				type: 'number',
				value: data.accommodation?.cost,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustBeGreaterOrEqual',
						value: 0,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
					{
						code: 'MustBeLessOrEqual',
						value: 1e8,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
				],
				label: t('forms:Cost'),
				required: false,
				visible: true,
				col: 12,
			},
			{
				controlName: 'childrenInFamily',
				type: 'number',
				label: t('forms:ChildrenInFamily'),
				value: data.accommodation?.childrenInFamily,
				displayFixedLabel: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^\\d{0,3}$',
						message: t('forms:ValidationMsgValueIntegerBetween', {
							min: 0,
							max: 100,
						}),
					},
				],
				required: false,
				visible: true,
				col: 12,
			},
			// {
			// 	controlName: 'childcareCost',
			// 	type: 'number',
			// 	label: t('forms:ChildcareCost'),
			// 	value: data.accommodation?.childcareCost,
			// 	displayFixedLabel: true,
			// 	validations: [
			// 		{
			// 			code: 'MustBeGreaterOrEqual',
			// 			value: 0,
			// 			message: t('forms:ValidationMsgValueBetween', {
			// 				min: 0,
			// 				max: 1e8,
			// 			}),
			// 		},
			// 		{
			// 			code: 'MustBeLessOrEqual',
			// 			value: 1e8,
			// 			message: t('forms:ValidationMsgValueBetween', {
			// 				min: 0,
			// 				max: 1e8,
			// 			}),
			// 		},
			// 	],
			// 	required: false,
			// 	col: 12,
			// 	visible: true,
			// },

			// {
			// 	controlName: 'employedSince',
			// 	type: 'datePicker',
			// 	label: t('forms:EmployedSince'),
			// 	minDate: yearsAgo(100),
			// 	maxDate: new Date(), // today
			// 	value: formatToStringLocale(data.employment?.employedSince),
			// 	required: true,
			// 	visible: true,
			// 	col: 12,
			// },
			// {
			// 	controlName: 'employerName',
			// 	type: 'text',
			// 	label: t('forms:EmployerName'),
			// 	value: data.employment?.employerName,
			// 	displayFixedLabel: true,
			// 	validations: [
			// 		{
			// 			code: 'MustMatchPattern',
			// 			value: '^.{0,100}$', // 0-100 chars
			// 			message: t('ValidationMsgMaxNChars', { n: 100 }),
			// 		},
			// 	],
			// 	required: false,
			// 	visible: true,
			// 	col: 12,
			// },
			// {
			// 	controlName: 'jobTitle',
			// 	type: 'dropdown',
			// 	label: t('forms:JobTitle'),
			// 	options: FormatEnumToList(
			// 		JobTitle,
			// 		PropertyType.JobTitle,
			// 		TranslationFile.Dropdown,
			// 	),
			// 	value: data.employment?.jobTitle,
			// 	required: false,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownJobTitle'),
			// },
			// {
			// 	controlName: 'industry',
			// 	type: 'dropdown',
			// 	label: t('forms:Industry'),
			// 	options: FormatEnumToList(
			// 		Industry,
			// 		PropertyType.Industry,
			// 		TranslationFile.Dropdown,
			// 	),
			// 	value: data.employment?.industry,
			// 	required: false,
			// 	visible: true,
			// 	col: 12,
			// 	wcagAriaLabel: t('forms:WCAG-Label_DropDownJobIndustry'),
			// },
			// {
			// 	options: [
			// 		{
			// 			value: true,
			// 			label: t('forms:Yes'),
			// 		},
			// 		{
			// 			value: false,
			// 			label: t('forms:No'),
			// 		},
			// 	],
			// 	required: true,
			// 	controlName: 'cashWithdrawal',
			// 	type: 'dropdown',
			// 	label: t('forms:CashWithdrawal'),
			// 	value: true, // data.card?.immediateCashWithdrawal,
			// 	col: 12,
			// },
			{
				controlName: 'cashWithdrawal',
				type: 'dropdown',
				label: t('forms:CashWithdrawal'),
				options: [
					{
						value: true,
						label: t('forms:Yes'),
					},
					{
						value: false,
						label: t('forms:No'),
					},
				],
				value: data.card?.immediateCashWithdrawal,
				required: true,
				visible: true,
				col: 12,
			},
		],
	}
}
